import * as React from 'react';
import { createPortal } from 'react-dom';
import { Check, ExternalLink } from 'lucide-react';
import { ApiError, type Feed } from '@proptr/api-client';
import { Button } from './ui/button.js';
import { commerceManagerUrl } from '../lib/feedUrl.js';
import { cn } from '../lib/utils.js';
import { client } from '../main.js';

// Publishing a feed to Meta: create a catalog in the brokerage's business
// portfolio and register the feed's public URL against it, after which
// Meta pulls on its own schedule.
//
// One component, two entry points — offered right after a feed is created,
// and from the feed row's menu afterwards. They must not drift: this
// writes a real asset into someone else's Meta business, and two
// implementations would eventually disagree about which business, or
// whether a second catalog gets created.

export function PublishToMetaModal({
  open,
  feed,
  onClose,
  onPublished,
}: {
  open: boolean;
  feed: Feed | null;
  onClose: () => void;
  onPublished?: () => void;
}) {
  // Fetched when the modal opens, never cached: portfolio access is
  // granted and revoked in Meta's UI without telling us, so anything we
  // held onto would eventually offer a business we can't write to.
  const [businesses, setBusinesses] = React.useState<
    Array<{ id: string; name: string }> | null
  >(null);
  const [businessId, setBusinessId] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [done, setDone] = React.useState<{
    catalogId: string;
    catalogName: string;
  } | null>(null);
  const [shown, setShown] = React.useState(false);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      // Reset everything per open: a previous run's success banner or
      // error shown over a different feed would be actively misleading.
      setError(null);
      setDone(null);
      setBusy(false);
      setBusinesses(null);
      setBusinessId(null);
      setShown(true);
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const r = requestAnimationFrame(() =>
        requestAnimationFrame(() => setVisible(true)),
      );
      return () => {
        cancelAnimationFrame(r);
        document.body.style.overflow = prev;
      };
    }
    setVisible(false);
    const t = setTimeout(() => setShown(false), 200);
    return () => clearTimeout(t);
  }, [open]);

  // Live portfolio lookup, per open.
  React.useEffect(() => {
    if (!open) return;
    let alive = true;
    void (async () => {
      try {
        const res = await fetch('/api/auth/meta/businesses', {
          credentials: 'include',
        });
        if (!alive) return;
        if (!res.ok) {
          // 409 = no connection. That's a state to explain, not an error
          // to shout about, so it resolves to an empty list.
          setBusinesses([]);
          return;
        }
        const body = (await res.json()) as {
          items: Array<{ id: string; name: string }>;
        };
        setBusinesses(body.items);
        // One business needs no choice; pre-select so the primary button
        // is immediately actionable.
        if (body.items.length === 1) setBusinessId(body.items[0]?.id ?? null);
      } catch {
        if (alive) setBusinesses([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, busy]);

  if (!shown || !feed) return null;

  const loading = businesses === null;
  const connected = (businesses ?? []).length > 0;
  const alreadyPublished = Boolean(feed.meta?.catalogId);

  const publish = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await client.feeds.publishToMeta(
        feed._id,
        businessId ?? undefined,
      );
      setDone(res.meta);
      onPublished?.();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : (err as Error).message,
      );
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Publish feed to Meta"
    >
      <div
        onClick={() => !busy && onClose()}
        className={cn(
          'absolute inset-0 bg-black/60 transition-opacity duration-200 ease-out',
          visible ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        className={cn(
          'relative w-full max-w-lg overflow-hidden rounded-2xl border border-[hsl(var(--hairline))] bg-card shadow-pop transition-all duration-200 ease-out',
          visible
            ? 'translate-y-0 scale-100 opacity-100'
            : 'translate-y-2 scale-95 opacity-0',
        )}
      >
        <div className="border-b border-[hsl(var(--hairline))] px-5 py-4">
          <h2 className="font-display text-[20px] font-medium leading-none tracking-[-0.01em]">
            {done ? 'Catalog created' : 'Publish to Meta'}
          </h2>
          <p className="mt-2 font-sans text-[13px] leading-snug text-muted-foreground">
            {done
              ? 'Meta will fetch this feed daily. Products appear once the first fetch completes.'
              : 'Creates a new catalog in your Meta business and points it at this feed. Your listings then stay in sync automatically.'}
          </p>
        </div>

        <div className="space-y-4 p-5">
          {done ? (
            <div className="rounded-xl border border-border bg-muted/40 p-4">
              <div className="font-sans text-[14px] font-semibold">
                {done.catalogName}
              </div>
              <a
                href={commerceManagerUrl(done.catalogId, businessId)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1.5 font-mono text-[11px] text-primary hover:underline"
              >
                Open in Commerce Manager
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          ) : loading ? (
            <p className="font-sans text-[13px] text-muted-foreground">
              Checking your Meta businesses…
            </p>
          ) : !connected ? (
            // No connection: say what to do rather than offering a button
            // whose only outcome is an error.
            <p className="font-sans text-[13px] leading-snug text-muted-foreground">
              No Meta business is connected yet. Connect one from the
              Marketing page first — the feed and its CSV URL work without
              it, you just have to add the URL in Commerce Manager by hand.
            </p>
          ) : alreadyPublished ? (
            <p className="font-sans text-[13px] leading-snug text-muted-foreground">
              This feed already feeds the catalog{' '}
              <span className="text-foreground">{feed.meta?.catalogName}</span>.
              Publishing again would create a second one.
            </p>
          ) : (
            <>
              {(businesses ?? []).length > 1 && (
                <div>
                  <div className="mono-label mb-2 text-muted-foreground">
                    Business
                  </div>
                  <div
                    role="radiogroup"
                    aria-label="Business portfolio"
                    className="space-y-2"
                  >
                    {(businesses ?? []).map((b) => {
                      const active = b.id === businessId;
                      return (
                        <button
                          key={b.id}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          onClick={() => setBusinessId(b.id)}
                          className={cn(
                            'flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors',
                            active
                              ? 'border-primary bg-primary/5 ring-2 ring-primary/25'
                              : 'border-border hover:border-primary/40 hover:bg-muted',
                          )}
                        >
                          <span className="min-w-0 truncate font-sans text-[13px]">
                            {b.name}
                          </span>
                          {active && (
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                              <Check className="h-3 w-3" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {(businesses ?? []).length === 1 && (
                <p className="font-sans text-[13px] leading-snug text-muted-foreground">
                  The catalog will be created in{' '}
                  <span className="text-foreground">{businesses?.[0]?.name}</span>.
                </p>
              )}
            </>
          )}

          {error && (
            <p className="font-sans text-[13px] leading-snug text-destructive">
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-[hsl(var(--hairline))] px-5 py-4">
          <Button type="button" variant="outline" onClick={onClose}>
            {done ? 'Done' : 'Not now'}
          </Button>
          {!done && !loading && connected && !alreadyPublished && (
            <Button
              type="button"
              onClick={() => void publish()}
              disabled={busy || !businessId}
            >
              {busy ? 'Creating…' : 'Create catalog'}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
